import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { ANALYTICAL_DATASET_WRITE_MUTATION_KEY } from '@verantos-dev/core-constants';
import { authFetch } from '@verantos-dev/core-utils';
import { cohortQueryKeys } from '@verantos-dev/data-access-cohorts';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { analyticalDatasetKeys } from './queryKeys';
import type {
  AnalyticalDatasetCohortEntry,
  AnalyticalDatasetExecution,
  AnalyticalDatasetProfile,
  AnalyticalDatasetVariableEntry,
} from './types';

interface ProcessAnalyticalDatasetInput {
  projectId: string;
  analyticalDatasetProfileId: string;
  /** The currently-loaded version id — the version to process / save target. */
  analyticalDatasetVersionId: string;
  /**
   * The staged definition. Pass only when there are unsaved changes — the backend
   * then persists it as a new version before dispatching. Omit when unchanged so
   * processing reuses the existing version (no version-history churn).
   */
  definition?: {
    cohorts: AnalyticalDatasetCohortEntry[];
    variables: AnalyticalDatasetVariableEntry[];
  };
}

/**
 * Dispatch a process run. With staged edits (`definition` provided), the backend
 * saves a new version then dispatches against it; without, it dispatches the existing
 * version as-is. Either way it inserts a PROCESSING execution row and returns it.
 */
export const useProcessAnalyticalDatasetMutation = () => {
  const queryClient = useQueryClient();
  // TODO: cleanup with skip_process_confirmation_modal ff — fast-disable switch
  // for the optimistic profile write below, independent of which caller
  // (useAdInlineProcess or useAdProcessModal) fired this mutation.
  const isInlineProcessEnabled = Boolean(useFeatureFlagSplit('skip_process_confirmation_modal'));

  return useMutation({
    // Tag so useProjectPageAnalyticalDatasetSync can bridge this write to the
    // project artifact manifest + activity feed via the mutation cache.
    mutationKey: [ANALYTICAL_DATASET_WRITE_MUTATION_KEY],
    mutationFn: ({
      projectId,
      analyticalDatasetProfileId,
      analyticalDatasetVersionId,
      definition,
    }: ProcessAnalyticalDatasetInput) =>
      authFetch<ObjectResponse<AnalyticalDatasetExecution>>(
        `/analytical-datasets/${encodeURIComponent(projectId)}` +
          `/analytical-dataset-profile/${encodeURIComponent(analyticalDatasetProfileId)}/process`,
        {
          method: 'POST',
          body: JSON.stringify({
            analyticalDatasetVersionId,
            // Only sent when dirty — keyed off the caller's hasStagedChanges.
            ...(definition ? { jsonValue: definition } : {}),
          }),
        }
      ),
    onSuccess: (_data, variables) => {
      // Dispatch flips the profile's latestStatus to PROCESSING, but then it
      // takes time to update on the backend — invalidating (and refetching) the
      // profile can cause a race condition and cache a stale pre-PROCESSING snapshot,
      // which then never resumes polling. Set it optimistically instead.
      // TODO: cleanup with skip_process_confirmation_modal ff
      if (isInlineProcessEnabled) {
        queryClient.setQueryData<ObjectResponse<AnalyticalDatasetProfile>>(
          analyticalDatasetKeys.profile(variables.analyticalDatasetProfileId),
          (prev) => (prev ? { ...prev, data: { ...prev.data, latestStatus: 'PROCESSING' } } : prev)
        );
      } else {
        queryClient.invalidateQueries({
          queryKey: analyticalDatasetKeys.profile(variables.analyticalDatasetProfileId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: analyticalDatasetKeys.list(variables.projectId),
      });
      queryClient.invalidateQueries({
        queryKey: cohortQueryKeys.assetStaleness(
          'analytical_dataset',
          variables.analyticalDatasetVersionId
        ),
      });
    },
    onError: (_error, variables) => {
      // The backend can commit a real status change (e.g. PROCESSING -> ERROR
      // on a dispatch failure) before the error ever reaches us — refetch so
      // the profile reflects that
      queryClient.invalidateQueries({
        queryKey: analyticalDatasetKeys.profile(variables.analyticalDatasetProfileId),
      });
    },
  });
};
